import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContentItem, ContentType } from "@/types/content";
import { HistoryPage } from "./HistoryPage";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { DashboardDataProvider } from "@/context/DashboardDataContext";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function HistoryPageContainer() {
  usePageMeta("History — Shattara AI", "", { noIndex: true });
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (event === "SIGNED_OUT" || !session) {
          navigate("/auth/signin");
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (!session) {
        navigate("/auth/signin");
      }
      
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!user) return;

      try {
        setLoading(true);

        const { data, error } = await supabase
          .from("content")
          .select(
            `
              id,
              title,
              type,
              thumbnail_url,
              source_url,
              storage_path,
              room_id,
              created_at,
              updated_at,
              visibility
            `
          )
          .order("created_at", { ascending: false });

        if (error) {
          console.error("[History] Error loading content:", error);
          return;
        }

        const mapped: ContentItem[] =
          data?.map((row: any) => ({
            id: row.id,
            title: row.title,
            type: (row.type as ContentType) ?? "text",
            thumbnailUrl: row.thumbnail_url ?? null,
            sourceUrl: row.source_url ?? null,
            storagePath: row.storage_path ?? null,
            roomId: row.room_id ?? null,
            createdAt: row.created_at,
            updatedAt: row.updated_at,
            visibility: row.visibility ?? "private",
          })) ?? [];

        setItems(mapped);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [user]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!session || !user) {
    return null;
  }

  return (
    <DashboardDataProvider>
      <DashboardShell>
        <main className="flex-1 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto w-full py-8">
            <HistoryPage items={items} isLoading={loading} />
          </div>
        </main>
      </DashboardShell>
    </DashboardDataProvider>
  );
}
